"use client";

import { FCC_SERVICE_LINE_FILENAME_ABBR } from "@/configuration/groups/fcc/configuration";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { formatIsoDate } from "@/src/lib/utils/date-and-phone-utils";
import { ServiceLine } from "@/src/models/constants/service-lines";
import { Referral, SchedulingPriority } from "@/src/models/documents/referral";
import { getPersonName } from "@/src/models/person";

const FCC_UPLOAD_FOLDER = "External Referrals";

interface FCCReferralInstructionsProps {
  referral: Referral;
}

function buildFccFileName(referral: Referral): string | null {
  const firstInitial = referral.patient?.givenName?.[0]?.toUpperCase();
  const lastName = referral.patient?.familyName ?? null;
  const serviceLineAbbr = referral.serviceLine
    ? FCC_SERVICE_LINE_FILENAME_ABBR[referral.serviceLine as ServiceLine]
    : undefined;
  const stateCode = referral.referringLocation?.address?.state?.trim().toUpperCase() || null;

  const parts = [firstInitial ? `${firstInitial}.` : null, lastName, serviceLineAbbr, stateCode].filter(
    (part): part is string => !!part
  );

  return parts.length > 0 ? parts.join(" ") : null;
}

export function FCCReferralInstructions({ referral }: FCCReferralInstructionsProps) {
  const referringProviderName = referral.referringProvider ? getPersonName(referral.referringProvider) : "";
  const referringProviderNpi = referral.referringProvider?.npiNumber ?? null;
  const referringLocationName = referral.referringLocation?.name ?? null;
  const referringLocationFax = referral.referringLocation?.faxPhoneNumber ?? null;
  const serviceLine = referral.serviceLine ?? null;
  const dateOfReferral = referral.dateOfReferral ? formatIsoDate(referral.dateOfReferral) : null;
  const schedulingPriority = referral.schedulingPriority ?? SchedulingPriority.ROUTINE;
  const fileName = buildFccFileName(referral);

  const isVaMilitary = !!referral.section?.reviewedContent?.isVaMilitaryReferral;

  return (
    <div className="flex flex-col gap-8 w-full sm:min-w-[800px] max-w-[1200px] mx-auto">
      <EhrInstructionsCard>
        <div className="flex flex-col gap-4 text-sm">
          <span className="font-medium text-center">FCC Referral Instructions:</span>
          {isVaMilitary && (
            <div className="flex items-center justify-center gap-2 rounded-md border border-yellow-400 bg-yellow-50 px-3 py-2 text-yellow-800 dark:border-yellow-600 dark:bg-yellow-950 dark:text-yellow-200">
              <Badge className="bg-yellow-500 text-white dark:bg-yellow-600">VA / Military / ADSM</Badge>
              <span className="font-medium">This is a VA / Military / ADSM referral. No Clinical Form Necessary.</span>
            </div>
          )}
          <p className="text-muted-foreground">
            Use the values below when entering this referral into the EMR. Click any value to copy.
          </p>

          <div className="space-y-3">
            <div>
              <span className="font-semibold text-foreground">Referring Provider</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {referringProviderName ? (
                    <CopyableContent text={referringProviderName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">NPI: </span>
                  {referringProviderNpi ? (
                    <CopyableContent text={referringProviderNpi} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="font-semibold text-foreground">Referring Location</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  {referringLocationName ? (
                    <CopyableContent text={referringLocationName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Fax: </span>
                  {referringLocationFax ? (
                    <CopyableContent text={referringLocationFax} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
              </div>
            </div>

            <div>
              <span className="font-semibold text-foreground">Referral Information</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">Service Line: </span>
                  {serviceLine ? (
                    <CopyableContent text={serviceLine} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Referral Date: </span>
                  {dateOfReferral ? (
                    <CopyableContent text={dateOfReferral} />
                  ) : (
                    <span className="text-muted-foreground italic">Not provided</span>
                  )}
                </div>
                <div>
                  <span className="text-muted-foreground">Scheduling Priority: </span>
                  <CopyableContent text={schedulingPriority} />
                </div>
              </div>
            </div>

            <div>
              <span className="font-semibold text-foreground">Document Upload</span>
              <div className="ml-4 mt-1 flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">File name: </span>
                  {fileName ? (
                    <CopyableContent text={fileName} />
                  ) : (
                    <span className="text-muted-foreground italic">Not enough info</span>
                  )}
                  <span className="text-muted-foreground text-xs ml-2">
                    (First Initial + Last Name + Service Type + State Abbreviation)
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Folder: </span>
                  <CopyableContent text={FCC_UPLOAD_FOLDER} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </EhrInstructionsCard>

      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </div>
  );
}
